<template>
  <v-app>
    <div class="container-login">
      <v-row class="container">
        <v-col style="display: flex; flex-direction: column; align-self: center; justify-self: center; padding-left: 200px;">

            <div class="picture">
              <img :src="setup.picture" alt="Logo" />
            </div>

            <h1 class="font-weight-medium mb-8" style="width: 60%">
              {{
                step < 3
                  ? $vuetify.lang.t('$vuetify.pages.login_page.enter_your_data')
                  : $vuetify.lang.t('$vuetify.label.change_password')
              }}
            </h1>

            <v-form ref="form" @submit.prevent="submit">
              <template v-if="step < 3">
                <template v-if="step === 1">
                  <v-text-field
                    id="login_username"
                    v-model="form.username"
                    outlined
                    hide-details="auto"
                    prepend-inner-icon="mdi-card-account-details-outline"
                    :error-messages="errors.username"
                    :class="step === 1 && keyboard ? '' : 'mb-8'"
                    :label="username.label"
                    :disabled="step !== 1"
                    :rules="username.rules"
                    :maxlength="providerLogin === 'UHC_ID_BOD' ? 15 : undefined"
                  />
                  <div class="username-keyboard"></div>
                </template>
                <template v-else>
                  <v-text-field
                    id="login_password"
                    v-model="form.password"
                    outlined
                    hide-details="auto"
                    :prepend-inner-icon="password.icon"
                    :error-messages="errors.password"
                    :class="step === 2 && keyboard ? '' : 'mb-8'"
                    :label="password.label"
                    :disabled="loading"
                    :rules="password.rules"
                    :type="password.type"
                    :append-icon="password.appendIcon"
                    @click:append="password.clickAppendIcon()"
                  />
                  <div class="password-keyboard"></div>
                </template>

                <v-btn
                  v-if="step === 1"
                  id="login_button_continue_step_1"
                  x-large
                  block
                  :loading="loading"
                  color="primary"
                  class="text-none pa-7"
                  type="submit"
                  >{{ $vuetify.lang.t('$vuetify.pages.login_page.continue') }}
                </v-btn>
                <v-row v-else>
                  <v-col cols="12" sm="6" md="6" lg="6">
                    <v-btn
                      id="login_back_button"
                      x-large
                      text
                      block
                      outlined
                      class="text-none pa-7"
                      type="button"
                      @click="back"
                      >{{ $vuetify.lang.t('$vuetify.btn.back') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="6" md="6" lg="6">
                    <v-btn
                      id="login_button_continue_step_2"
                      x-large
                      block
                      :loading="loading"
                      color="primary"
                      class="text-none pa-7"
                      type="submit"
                      >{{ $vuetify.lang.t('$vuetify.pages.login_page.continue') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </template>
              <template v-else>
                <v-text-field
                  id="login_newPassword"
                  v-model="form.new_password"
                  outlined
                  hide-details="auto"
                  prepend-inner-icon="vpn_key"
                  type="password"
                  :error-messages="errors.new_password"
                  :class="step === 3 && keyboard ? '' : 'mb-8'"
                  :label="$vuetify.lang.t('$vuetify.pages.login_page.new_password')"
                  :disabled="loading"
                  :rules="[
                    (value) => value.length >= 8 || $vuetify.lang.t('$vuetify.pages.login_page.password_min_length'),
                    (value) =>
                      /^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{6,}$/.test(value) ||
                      $vuetify.lang.t('$vuetify.pages.login_page.valid_password'),
                  ]"
                />
                <div class="password-new-keyboard"></div>
                <v-btn
                  id="login_button_continue_step_3"
                  x-large
                  block
                  :loading="loading"
                  color="primary"
                  class="text-none pa-7"
                  type="submit"
                  >{{ $vuetify.lang.t('$vuetify.label.confirm') }}
                </v-btn>
              </template>
            </v-form>
          <primeit-message-snackbar />
        </v-col>

        <v-col style="display: flex; flex-direction: column; align-self: center; justify-self: center; padding-left: 200px;">
          <div class="max-width text-center mt-14" v-show="step === 1">
            <p class="font-weight-medium mt-4">
              {{ $vuetify.lang.t('$vuetify.pages.login_page.dont_have_account') }}
            </p>

            <canvas id="canvas" style="width: 300px; height: 300px;"></canvas>

            <p class="font-weight-medium mt-4">
              {{ $vuetify.lang.t('$vuetify.btn.register') }}
            </p>
          </div>
        </v-col>
      </v-row>

    </div>
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex';
import { CommonSecurity } from '@primeit/components-vue';
import { isValid } from 'cpf';
import * as cns from '@/utils/cns';
import isEmail from 'validator/lib/isEmail';
import moment from 'moment';
import QRCode from 'qrcode';

import keyboard from '../../../mixins/keyboard';

export default {
  mixins: [keyboard],
  data: () => ({
    step: 1,
    loading: false,
    showPassword: false,
    form: {
      username: '',
      password: '',
      new_password: '',
    },
    errors: {
      username: [],
      password: [],
      new_password: [],
    },
  }),
  computed: {
    ...mapGetters({
      setup: 'credential/SETUP',
      feedback: 'common/FEEDBACK',
      checkEmail: 'credential/CHECK_EMAIL',
      checkPassword: 'credential/CHECK_PASSWORD',
      changePassword: 'credential/CHANGE_PASSWORD',
    }),
    providerLogin() {
      return this.setup?.provider?.security?.login?.totem || 'DEFAULT';
    },
    username() {
      let field;

      if (this.providerLogin === 'DEFAULT') {
        field = {
          label: this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_email'),
          rules: [
            (value) => !!value || this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_email'),
            (value) => isEmail(value) || this.$vuetify.lang.t('$vuetify.pages.login_page.invalid_email'),
          ],
        };
      } else if (this.providerLogin === 'CPF_BOD') {
        field = {
          label: this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_cpf'),
          rules: [
            (value) => !!value || this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_cpf'),
            (value) => isValid(value) || this.$vuetify.lang.t('$vuetify.pages.login_page.invalid_cpf'),
          ],
        };
      } else if (this.providerLogin === 'UHC_ID_BOD') {
        field = {
          label: this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_universal_healthcare_id'),
          rules: [
            (value) => !!value || this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_universal_healthcare_id'),
            (value) =>
              cns.isValid(value) || this.$vuetify.lang.t('$vuetify.pages.login_page.invalid_universal_healthcare_id'),
          ],
        };
      }

      return field;
    },
    password() {
      let field;

      if (this.providerLogin === 'DEFAULT') {
        field = {
          icon: 'mdi-key',
          label: this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_password'),
          rules: [
            (value) => !!value || this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_password'),
            (value) => value.length >= 5 || this.$vuetify.lang.t('$vuetify.pages.login_page.invalid_password'),
          ],
          type: this.showPassword ? 'text' : 'password',
          appendIcon: this.showPassword ? 'mdi-eye-off' : 'mdi-eye',
          clickAppendIcon: () => (this.showPassword = !this.showPassword),
        };
      } else if (this.providerLogin === 'CPF_BOD' || this.providerLogin === 'UHC_ID_BOD') {
        field = {
          icon: 'mdi-calendar',
          label: this.$vuetify.lang.t('$vuetify.pages.login_page.type_your_birthdate'),
          rules: [
            (value) => !!value || this.$vuetify.lang.t('$vuetify.pages.login_page.birthdate_is_required'),
            (value) =>
              moment(value, 'DD/MM/YYYY').isValid() ||
              this.$vuetify.lang.t('$vuetify.pages.login_page.invalid_birthdate'),
          ],
          type: 'text',
        };
      }

      return field;
    },
    backgroundColorLogin() {
      return this.setup?.theme?.login?.light[0]?.value || 'white';
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.resetValidation();
      },
    },
    checkEmail(val) {
      if (val?.result) {
        this.$refs.form.resetValidation();
        this.step = 2;
        this.$nextTick(() => {
          this.createPasswordKeyboard();
        });
      } else {
        this.errors.username = val.message ?? [this.$vuetify.lang.t('$vuetify.pages.login_page.user_not_found')];
        this.createUsernameKeyboard();
      }

      this.loading = false;
    },
    checkPassword(val) {
      if (val) {
        this.loading = false;
        switch (val.result) {
          case 'NEW_PASSWORD_REQUIRED':
            this.$refs.form.resetValidation();
            this.step = 3;
            this.$nextTick(() => this.createPasswordNewKeyboard());
            break;
          case 'OK':
            this.$router.push({ path: '/emergency/person/', query: this.$route.query });
            break;
          case 'ERROR':
            this.errors.password = [
              this.providerLogin === 'DEFAULT'
                ? this.$vuetify.lang.t('$vuetify.pages.login_page.invalid_password')
                : this.$vuetify.lang.t('$vuetify.pages.login_page.invalid_date_of_birth'),
            ];
            this.createPasswordKeyboard();
            break;
        }
      }
    },
    changePassword(val) {
      if (val) {
        this.loading = false;
        switch (val.result) {
          case 'OK':
            this.$router.push({ path: '/emergency/person/', query: this.$route.query });
            break;
          case 'ERROR':
            this.errors.new_password = [this.$vuetify.lang.t('$vuetify.pages.login_page.valid_password')];
            break;
        }
      }
    },
  },
  async created() {
    await CommonSecurity.signOut();
  },
  mounted() {
    QRCode.toCanvas(document.getElementById('canvas'), `${document.location.host}/signup`, function (error) {
      if (error) console.error(error);
      console.log('success!');
    });

    const seo = this.setup?.landing?.seo;
    if (seo) {
      document.head.querySelector('meta[name="description"]').content = seo.description;
      document.head.querySelector('meta[name="keywords"]').content = seo.meta_tags;
    }

    document.title = this.setup?.name;
    this.createUsernameKeyboard();
  },
  methods: {
    isAllowedToRegister() {
      return this.setup?.settings?.allow_patient_to_register;
    },
    async back() {
      await this.destroyKeyboard(0);
      this.step = 1;
      this.$nextTick(() => {
        this.createUsernameKeyboard();
      });
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    createUsernameKeyboard() {
      if (this.providerLogin === 'DEFAULT') {
        this.createKeyboard({
          form: this.form,
          field: 'username',
          class: 'username-keyboard',
          type: 'text',
        });
      } else if (this.providerLogin === 'CPF_BOD' || this.providerLogin === 'UHC_ID_BOD') {
        this.createKeyboard({
          form: this.form,
          field: 'username',
          class: 'username-keyboard',
          mask: this.providerLogin === 'CPF_BOD' ? '999.999.999-99' : '999999999999999',
        });
      }
    },
    createPasswordKeyboard() {
      if (this.providerLogin === 'DEFAULT') {
        this.createKeyboard({
          form: this.form,
          field: 'password',
          class: 'password-keyboard',
          type: 'text',
        });
      } else if (this.providerLogin === 'CPF_BOD' || this.providerLogin === 'UHC_ID_BOD') {
        this.createKeyboard({
          form: this.form,
          field: 'password',
          class: 'password-keyboard',
          mask: '99/99/9999',
        });
      }
    },
    createPasswordNewKeyboard() {
      if (this.providerLogin === 'DEFAULT') {
        this.createKeyboard({
          form: this.form,
          field: 'new_password',
          class: 'password-new-keyboard',
          type: 'text',
        });
      } else if (this.providerLogin === 'CPF_BOD' || this.providerLogin === 'UHC_ID_BOD') {
        this.createKeyboard({
          form: this.form,
          field: 'new_password',
          class: 'password-new-keyboard',
          mask: '99/99/9999',
        });
      }
    },
    async submit() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        this.destroyKeyboard(0);

        // Checar username
        if (this.step === 1) {
          this.$store.dispatch('credential/CHECK_EMAIL', {
            email: this.providerLogin === 'DEFAULT' ? this.form.username : this.form.username.replaceAll(/[.-]/g, ''),
          });
        } else if (this.step === 2) {
          let password;

          if (this.providerLogin === 'DEFAULT') {
            password = this.form.password;
          } else {
            const [DD, MM, YYYY] = this.form.password.split('/');
            password = `${YYYY}-${MM}-${DD}`;
          }

          sessionStorage.setItem('ENTRY_TIME', moment().toISOString());
          this.$store.dispatch('credential/CHECK_PASSWORD', { password });
        } else {
          this.$store.dispatch('credential/CHANGE_PASSWORD', {
            newPassword: this.form.new_password,
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.container-login {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.picture {
  width: 100%;
  margin-bottom: 3.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: v-bind('backgroundColorLogin');

  img {
    width: 100%;
  }
}

.v-form {
  width: 100%;
  .username-keyboard,
  .password-keyboard,
  .password-new-keyboard {
    margin: 1rem 0 1.5rem 0;
  }
}

@media only screen and (max-width: 1440px) {
  .picture {
    width: 40%;
  }

  .v-form {
    width: 60%;
  }
}

@media only screen and (max-width: 1024px) {
  .picture {
    width: 50%;
  }

  h1 {
    font-size: 1.8rem;
  }

  .v-form {
    width: 60%;
  }
}

@media only screen and (max-width: 768px) {
  h1 {
    font-size: 1.6rem;
  }
  .v-form {
    width: 80%;
  }
}
</style>
